import { createTheme } from '@mui/material/styles';

const DARK = {
  bg: { default: '#0F1117', paper: '#1A1D2E' },
  text: { primary: '#E8EAED', secondary: '#9AA0B4' },
  link: '#8B84FF',
  divider: 'rgba(255,255,255,0.08)',
  action: { hover: 'rgba(108,99,255,0.08)', selected: 'rgba(108,99,255,0.16)' },
  appBar: '#1A1D2E',
  appBarBorder: 'rgba(255,255,255,0.08)',
  drawer: '#13151F',
  drawerBorder: 'rgba(255,255,255,0.06)',
  card: 'rgba(255,255,255,0.04)',
  cardShadow: '0 4px 24px rgba(0,0,0,0.4)',
  autofillBg: '#1A1D2E',
  autofillText: '#E8EAED',
  inputBorder: 'rgba(255,255,255,0.12)',
  disabledText: 'rgba(232,234,237,0.45)',
  disabledLabel: 'rgba(154,160,180,0.55)',
  tableHead: 'rgba(108,99,255,0.12)',
};

const LIGHT = {
  bg: { default: '#f0f2f5', paper: '#ffffff' },
  text: { primary: '#1a1a2e', secondary: '#5c6b8a' },
  link: '#5a52d5',
  divider: 'rgba(0,0,0,0.10)',
  action: { hover: 'rgba(108,99,255,0.06)', selected: 'rgba(108,99,255,0.10)' },
  appBar: '#ffffff',
  appBarBorder: 'rgba(0,0,0,0.08)',
  drawer: '#f8f9fc',
  drawerBorder: 'rgba(0,0,0,0.08)',
  card: 'rgba(0,0,0,0.015)',
  cardShadow: '0 2px 12px rgba(0,0,0,0.07)',
  autofillBg: '#ffffff',
  autofillText: '#1a1a2e',
  inputBorder: 'rgba(0,0,0,0.20)',
  disabledText: 'rgba(26,26,46,0.38)',
  disabledLabel: 'rgba(92,107,138,0.6)',
  tableHead: 'rgba(108,99,255,0.08)',
};

export function getTheme(mode = 'dark') {
  const c = mode === 'dark' ? DARK : LIGHT;
  return createTheme({
    palette: {
      mode,
      background: c.bg,
      primary: { main: '#6C63FF', light: '#8B84FF', dark: '#4A44CC' },
      secondary: { main: '#00D4AA' },
      text: c.text,
      divider: c.divider,
      action: c.action,
    },
    typography: {
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          * { transition: border-color 0.2s; }
          body { transition: background-color 0.2s; }
          a:not([class]) { color: ${c.link}; text-decoration: none; }
          a:not([class]):hover { text-decoration: underline; }
          ::-webkit-scrollbar { width: 6px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb { background: rgba(108,99,255,0.4); border-radius: 3px; }
          input:-webkit-autofill,
          input:-webkit-autofill:hover,
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 1000px ${c.autofillBg} inset !important;
            -webkit-text-fill-color: ${c.autofillText} !important;
            caret-color: ${c.autofillText};
            transition: background-color 5000s ease-in-out 0s;
          }
        `,
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            background: c.card,
            backdropFilter: 'blur(12px)',
            border: `1px solid ${c.divider}`,
            boxShadow: c.cardShadow,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10, textTransform: 'none', fontWeight: 600, padding: '8px 20px' },
          contained: {
            background: 'linear-gradient(135deg, #6C63FF 0%, #8B84FF 100%)',
            boxShadow: '0 4px 14px rgba(108,99,255,0.4)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 20px rgba(108,99,255,0.5)',
              background: 'linear-gradient(135deg, #6C63FF 0%, #8B84FF 100%)',
            },
          },
          outlined: {
            borderColor: 'rgba(108,99,255,0.5)',
            color: '#4B3FE0',
            '&:hover': { borderColor: '#6C63FF', background: 'rgba(108,99,255,0.08)' },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.inputBorder },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(108,99,255,0.5)' },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: c.inputBorder },
          root: {
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(108,99,255,0.5)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6C63FF' },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: { '&.Mui-disabled': { WebkitTextFillColor: c.disabledText, opacity: 1 } },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { '&.Mui-disabled': { color: c.disabledLabel } },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: { '&.Mui-disabled': { WebkitTextFillColor: c.disabledText } },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 8 } } },
      MuiTab: {
        styleOverrides: {
          root: {
            color: c.text.secondary,
            textTransform: 'none',
            fontWeight: 500,
            '&.Mui-selected': { color: '#4B3FE0' },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: { root: { background: c.tableHead } },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { background: c.tableHead, color: c.text.primary, fontWeight: 700 },
        },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 4, height: 6 } },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { background: c.appBar, boxShadow: `0 1px 0 ${c.appBarBorder}` },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { background: c.drawer, borderRight: `1px solid ${c.drawerBorder}` },
        },
      },
    },
  });
}

// Default export for backwards compat (dark theme)
export default getTheme('dark');
